import { Link } from 'react-router-dom';
import {
  ShieldCheckIcon,
  MapPinIcon,
  ClockIcon,
  PhoneIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '../ui/Card';
import { cn } from '../../utils/cn';
import { getFullImageUrl, getInitials } from '../../utils/imageUrl';
import type { ProtectedPerson as APIProtectedPerson } from '../../services/executive-protection.service';
import type { LocationData } from '../../utils/dataTransform';

// Helper function to format LocationData object to readable string
const formatLocation = (location: LocationData | null | undefined): string => {
  if (!location) {return '';}
  return `${location.latitude.toFixed(4)}°, ${location.longitude.toFixed(4)}°`;
};

// Extended type for component compatibility
export interface ProtectedPerson extends APIProtectedPerson {
  lastLocation?: string;
  securityDetail?: string;
}

interface ProtectedPersonCardProps {
  person: ProtectedPerson;
}

export function ProtectedPersonCard({ person }: ProtectedPersonCardProps) {
  const riskColors: Record<string, string> = {
    low: 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300',
    medium: 'bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300',
    high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300',
    critical: 'bg-danger-100 text-danger-800 dark:bg-danger-900/20 dark:text-danger-300',
  };

  const statusColors: Record<string, string> = {
    active: 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300',
    travel: 'bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300',
    inactive: 'bg-secondary-100 text-secondary-800 dark:bg-secondary-800 dark:text-secondary-300',
  };

  const timeAgo = (date: string) => {
    const now = new Date();
    const updateDate = new Date(date);
    const diffInMinutes = Math.floor((now.getTime() - updateDate.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) {return 'Just now';}
    if (diffInMinutes < 60) {return `${diffInMinutes}m ago`;}
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {return `${diffInHours}h ago`;}
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const isInactive = person.status === 'inactive';
  const profileImageUrl = getFullImageUrl(person.profilePictureUrl);

  return (
    <Link
      to={`/security/executive-protection/${person.id}`}
      className={cn(
        "block h-full",
        isInactive && "pointer-events-none"
      )}
    >
      <Card hover={isInactive ? 'none' : 'default'} className={cn("h-full transition-all duration-200", isInactive && "opacity-60")}>
      <CardContent className="p-6">
        {/* Inactive Overlay */}
        {isInactive && (
          <div className="absolute top-2 left-2 z-10">
            <span className="px-2 py-1 text-xs font-bold bg-secondary-500 text-white rounded">
              INACTIVE
            </span>
          </div>
        )}

        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            {profileImageUrl ? (
              <img
                src={profileImageUrl}
                alt={person.name}
                className={cn(
                  "w-12 h-12 rounded-full object-cover border-2",
                  isInactive
                    ? "border-secondary-300 dark:border-secondary-600 grayscale"
                    : "border-primary-200 dark:border-primary-800"
                )}
              />
            ) : (
              <div className={cn(
                "w-12 h-12 rounded-full flex items-center justify-center",
                isInactive
                  ? "bg-secondary-400 dark:bg-secondary-600"
                  : "bg-gradient-to-br from-primary-400 to-primary-600"
              )}>
                <span className="text-white font-semibold text-sm">
                  {getInitials(person.name)}
                </span>
              </div>
            )}
            <div>
              <h3 className={cn(
                "font-semibold text-lg",
                isInactive
                  ? "text-secondary-500 dark:text-secondary-500 line-through"
                  : "text-secondary-900 dark:text-white"
              )}>
                {person.name ?? 'Unknown'}
              </h3>
              <p className="text-secondary-600 dark:text-secondary-400 text-sm">
                {person.title ?? 'N/A'}
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          <span className={cn('px-3 py-1 text-xs font-medium rounded-full', riskColors[person.riskLevel] ?? riskColors.medium)}>
            {person.riskLevel.toUpperCase()} RISK
          </span>
          <span className={cn('px-3 py-1 text-xs font-medium rounded-full', statusColors[person.status ?? 'active'] ?? statusColors.active)}>
            {(person.status ?? 'active').replace('_', ' ').toUpperCase()}
          </span>
        </div>

        <div className="space-y-3">
          {person.lastLocation && (
            <div className="flex items-center space-x-2 text-sm">
              <MapPinIcon className="w-4 h-4 text-secondary-500 dark:text-secondary-400" />
              <span className="text-secondary-700 dark:text-secondary-300">
                {person.lastLocation}
              </span>
            </div>
          )}
          {!person.lastLocation && person.location && (
            <div className="flex items-center space-x-2 text-sm">
              <MapPinIcon className="w-4 h-4 text-secondary-500 dark:text-secondary-400" />
              <span className="text-secondary-700 dark:text-secondary-300">
                {formatLocation(person.location)}
              </span>
            </div>
          )}

          {person.lastUpdate && (
            <div className="flex items-center space-x-2 text-sm">
              <ClockIcon className="w-4 h-4 text-secondary-500 dark:text-secondary-400" />
              <span className="text-secondary-700 dark:text-secondary-300">
                Updated {timeAgo(person.lastUpdate)}
              </span>
            </div>
          )}

          {person.department && (
            <div className="flex items-center space-x-2 text-sm">
              <BuildingOfficeIcon className="w-4 h-4 text-secondary-500 dark:text-secondary-400" />
              <span className="text-secondary-700 dark:text-secondary-300">
                {person.department}
              </span>
            </div>
          )}

          {person.securityDetail && (
            <div className="flex items-center space-x-2 text-sm">
              <ShieldCheckIcon className="w-4 h-4 text-secondary-500 dark:text-secondary-400" />
              <span className="text-secondary-700 dark:text-secondary-300">
                {person.securityDetail}
              </span>
            </div>
          )}

          {person.phone && (
            <div className="flex items-center space-x-2 text-sm">
              <PhoneIcon className="w-4 h-4 text-secondary-500 dark:text-secondary-400" />
              <span className="text-secondary-700 dark:text-secondary-300">
                {person.phone}
              </span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
    </Link>
  );
}

export default ProtectedPersonCard;
